import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { marketCategoryLabel } from '../../lib/marketCategories';
import { aiBudget, aiBudgetUpdate, BUDGET_FIELDS, ingestControl, ingestRetry, ingestSettings, ingestStatus, PACE_FIELDS } from '../../lib/marketAdminIngestion';

// Continuous catalog ingestion (staff). Is it running, what did it add today,
// what has it spent against which limit, what failed, what waits for a person.
// The limits here are MAABAR's own: the pipeline stops itself BEFORE a call
// that would cross them. They are not a provider guarantee — a provider bills
// what it bills (late usage data, the same key used elsewhere), which the page
// says next to the numbers.

const fmt = (v, d = 0) => (v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d }));
const fmtDate = (v) => (v ? String(v).slice(0, 16).replace('T', ' ') : '—');
const pct = (used, cap) => (cap > 0 ? Math.min(100, Math.round((Number(used) / Number(cap)) * 100)) : 0);

const T = {
  ar: {
    title: 'جلب المنتجات التلقائي', sub: 'يكتشف منتجات جديدة عبر الأقسام، يفحصها ويصنّفها، ويحدّث الأسعار والتوفر — ضمن ميزانية تحددونها. لا يُنشر منتج إلا وفق قواعد النشر أدناه.',
    state: 'الحالة', running: 'يعمل', stopped: 'متوقف', paused: 'موقوف مؤقتًا', emergency: 'إيقاف طارئ', disabled: 'غير مفعّل', stale: 'لا توجد دورة ناجحة منذ ٣٠ دقيقة',
    lastRun: 'آخر دورة', reason: 'السبب', public: 'السوق للعامة', on: 'مفعّل', off: 'غير مفعّل',
    importedToday: 'أُضيف اليوم', publishedToday: 'نُشر تلقائيًا اليوم', discoveredToday: 'اكتُشف اليوم', rejectedToday: 'استُبعد اليوم', syncedToday: 'حُدّث اليوم',
    review: 'بانتظار المراجعة', failed: 'مهام متعثرة', staleSync: 'تحتاج تحديثًا', apiMinute: 'طلبات HioBuy آخر دقيقة',
    spend: 'الإنفاق مقابل الحدود', today: 'اليوم', month: 'هذا الشهر', remaining: 'المتبقي', perImport: 'تكلفة المنتج المضاف (تقديرية)',
    budgetNote: 'هذه حدود يفرضها معبر قبل كل طلب مدفوع (بما في ذلك الطلبات الجارية). ليست سقفًا يضمنه المزوّد: قد تصل بيانات الفوترة متأخرة، وقد يُستخدم المفتاح نفسه خارج هذا النظام. وحدات HioBuy تُقاس بوحدات المزوّد؛ تحويلها إلى دولار يحتاج سعر الوحدة من الإدارة.',
    controls: 'التحكم', enable: 'تفعيل الجلب', disable: 'إيقاف التفعيل', pause: 'إيقاف مؤقت', resume: 'استئناف', stop: 'إيقاف طارئ', release: 'رفع الإيقاف الطارئ', runNow: 'جدولة دورة الآن',
    discovery: 'الاكتشاف', sync: 'التحديث', autoPublish: 'النشر التلقائي للمنتجات المستوفية', budgets: 'الميزانية', pace: 'الوتيرة', save: 'حفظ', saved: 'حُفظ', confirmStop: 'إيقاف كل الجلب فورًا؟',
    confirmEnable: 'تفعيل الجلب يبدأ استهلاك وحدات HioBuy ورصيد Gemini ضمن الحدود المحددة. هل اعتُمدت الميزانية؟',
    queue: 'قائمة المهام', kind: { discover: 'اكتشاف', import: 'استيراد', sync: 'تحديث' }, st: { queued: 'بالانتظار', running: 'قيد التنفيذ', done: 'منجزة', failed: 'ستُعاد', dead: 'متعثرة', skipped: 'متجاوزة' },
    dead: 'المهام المتعثرة', retry: 'إعادة المحاولة', none: 'لا شيء', categories: 'الأقسام الأسرع نموًا (٧ أيام)', added: 'أُضيف', total: 'الإجمالي',
    daily: 'الإنفاق اليومي (٣٠ يومًا)', units: 'وحدة', rules: 'قواعد النشر', reviewLink: 'افتح قائمة المراجعة',
    rulesText: 'لا يُنشر تلقائيًا ما فيه: احتمال تقليد أو علامة فاخرة، ادعاء صحي، قسم منظَّم، هوية مورد غير معروفة، ترجمة ناقصة، أقل من ٣ صور أو صور ترويجية فقط، شحنة يرفضها خط السعودية (بطاريات، سوائل، مغناطيس، وزن فوق ١٠ كجم)، تصنيف غير مؤكد، سعر مفقود، احتمال تكرار. المحظور في المملكة يُستبعد قبل الاستيراد. ما سوى ذلك ينتظر مراجعة شخص.',
  },
  en: {
    title: 'Automatic catalog ingestion', sub: 'Discovers new products across the categories, screens and classifies them, and refreshes prices and availability — within a budget you set. Nothing is published except by the rules below.',
    state: 'State', running: 'Running', stopped: 'Stopped', paused: 'Paused', emergency: 'Emergency stop', disabled: 'Not enabled', stale: 'No successful run in 30 minutes',
    lastRun: 'Last run', reason: 'Reason', public: 'Public marketplace', on: 'On', off: 'Off',
    importedToday: 'Imported today', publishedToday: 'Auto-published today', discoveredToday: 'Discovered today', rejectedToday: 'Rejected today', syncedToday: 'Synced today',
    review: 'Awaiting review', failed: 'Failed jobs', staleSync: 'Due for sync', apiMinute: 'HioBuy calls, last minute',
    spend: 'Spend against limits', today: 'Today', month: 'This month', remaining: 'Remaining', perImport: 'Cost per imported product (estimated)',
    budgetNote: 'These are limits MAABAR enforces before every billable call (in-flight calls included). They are not a cap the provider guarantees: billing data can arrive late, and the same key may be used outside this system. HioBuy is measured in its own units; USD needs the per-unit price from the owner.',
    controls: 'Controls', enable: 'Enable ingestion', disable: 'Disable', pause: 'Pause', resume: 'Resume', stop: 'Emergency stop', release: 'Lift emergency stop', runNow: 'Schedule a run now',
    discovery: 'Discovery', sync: 'Sync', autoPublish: 'Auto-publish products that pass every rule', budgets: 'Budget', pace: 'Pace', save: 'Save', saved: 'Saved', confirmStop: 'Stop all ingestion now?',
    confirmEnable: 'Enabling starts spending HioBuy units and Gemini credit within the limits set. Has the budget been approved?',
    queue: 'Queue', kind: { discover: 'Discover', import: 'Import', sync: 'Sync' }, st: { queued: 'Queued', running: 'Running', done: 'Done', failed: 'Will retry', dead: 'Dead', skipped: 'Skipped' },
    dead: 'Dead jobs', retry: 'Retry', none: 'None', categories: 'Fastest-growing categories (7 days)', added: 'Added', total: 'Total',
    daily: 'Daily spend (30 days)', units: 'units', rules: 'Publication rules', reviewLink: 'Open the review queue',
    rulesText: 'Never auto-published: counterfeit or luxury-brand risk, health claims, regulated sectors, unknown supplier identity, missing translation, fewer than 3 images or promotional-only images, cargo the Saudi line refuses (batteries, liquids, magnets, over 10 kg), an uncertain category, no price, a possible duplicate. Items prohibited in Saudi Arabia are excluded before import. Everything else waits for a person.',
  },
};

function Meter({ label, used, cap, unit, d = 0 }) {
  const p = pct(used, cap);
  return (
    <div data-meter={label} style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
        <span>{label}</span>
        <span dir="ltr" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(used, d)} / {fmt(cap, d)} {unit}</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--ac-sunk)', overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: p >= 100 ? 'var(--ac-danger)' : p >= 80 ? 'var(--ac-warn)' : 'var(--ac-ok)' }} />
      </div>
    </div>
  );
}

function Kpi({ n, label, tone, k }) {
  return (
    <div className="ac-kpi" data-kpi={k} style={{ cursor: 'default' }}>
      <div className={`ac-kpi-num${tone ? ` ${tone}` : ''}`}>{fmt(n)}</div>
      <p className="ac-kpi-lbl">{label}</p>
    </div>
  );
}

const UNIT = { units: ['وحدة', 'units'], hours: ['ساعة', 'hours'], USD: ['USD', 'USD'], '0–1': ['0–1', '0–1'] };
function NumberFields({ fields, s, draft, setDraft, isAr }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
      {fields.map(([k, ar, en, unit]) => (
        <label key={k} className="ac-field">
          <span style={{ fontSize: 12, color: 'var(--ac-muted)' }}>{isAr ? ar : en}{unit ? <span style={{ marginInlineStart: 4, color: 'var(--ac-faint)' }}>· <bdi>{UNIT[unit]?.[isAr ? 0 : 1] ?? unit}</bdi></span> : null}</span>
          <input className="ac-input" dir="ltr" inputMode="decimal" data-setting={k} value={draft[k] ?? (s[k] ?? '')} onChange={(e) => setDraft((x) => ({ ...x, [k]: e.target.value }))} />
        </label>
      ))}
    </div>
  );
}

export default function ConsoleMarketIngestion({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const c = T[isAr ? 'ar' : 'en'];
  const [st, setSt] = useState(null);
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState({});
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };
  const load = useCallback(async () => {
    const r = await ingestStatus();
    if (r.ok) { setSt(r.data); setErr(''); } else setErr(r.error);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const control = async (action) => {
    if (action === 'stop' && !window.confirm(c.confirmStop)) return;
    const r = await ingestControl(action);
    flash(r.ok ? (action === 'schedule' ? `${c.runNow}: ${r.data?.enqueued ?? 0}` : c.saved) : r.error);
    load();
  };
  const patch = async (p) => {
    if (p.enabled === true && !window.confirm(c.confirmEnable)) return;
    const r = await ingestSettings(p);
    flash(r.ok ? c.saved : r.error);
    if (r.ok) setDraft({});
    load();
  };
  const saveNumbers = () => {
    const p = {};
    for (const [k, v] of Object.entries(draft)) p[k] = v === '' ? null : Number(v);
    if (Object.keys(p).length) patch(p);
  };

  const s = st?.settings ?? {};
  const sp = st?.spend ?? {};
  const state = !st ? null : s.emergency_stop ? ['danger', c.emergency] : !s.enabled ? ['neutral', c.disabled] : s.paused ? ['warn', c.paused] : st.running ? ['ok', c.running] : ['warn', c.stale];
  const queue = st?.queue ?? {};
  const kinds = ['discover', 'import', 'sync'];
  const statuses = ['queued', 'running', 'failed', 'dead', 'done'];
  const usdPerImport = sp.imports_month > 0 ? Number(sp.usd_month) / Number(sp.imports_month) : null;

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-ingestion">
      <div className="ac-page" data-ingestion-page="1" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{c.title}</h1>
            <p className="ac-sub">{c.sub}</p>
          </div>
          {state && <span className={`ac-badge ${state[0]}`} data-ingest-state={state[0]}><span className="dot" />{state[1]}</span>}
        </div>
        {err && <div className="ac-placeholder" data-ingest-error="1">{err}</div>}
        {!st && !err && <div className="ac-skel" style={{ height: 120 }} />}
        <AiBudgetCard isAr={isAr} />
        {st && (
          <>
            <div className="ac-card">
              <h2 className="ac-card-title">{c.state}</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, fontSize: 13 }}>
                <span>{c.lastRun}: <b dir="ltr">{fmtDate(st.last_run?.started_at)}</b>{st.last_run?.outcome ? ` · ${st.last_run.outcome}` : ''}</span>
                {s.paused && <span data-paused-reason="1">{c.reason}: <b dir="ltr">{s.paused_reason}</b></span>}
                <span>{c.public}: <b>{st.public ? c.on : c.off}</b></span>
              </div>
            </div>

            <div className="ac-kpi-grid" style={{ marginTop: 16 }}>
              <Kpi k="imported" n={st.today?.imported} label={c.importedToday} />
              <Kpi k="published" n={st.today?.published} label={c.publishedToday} />
              <Kpi k="discovered" n={st.today?.discovered} label={c.discoveredToday} />
              <Kpi k="rejected" n={st.today?.rejected} label={c.rejectedToday} />
              <Kpi k="synced" n={st.today?.synced} label={c.syncedToday} />
              <Kpi k="review" n={st.pending_review} label={c.review} tone={st.pending_review > 0 ? 'attn' : ''} />
              <Kpi k="failed" n={st.failed_jobs} label={c.failed} tone={st.failed_jobs > 0 ? 'hot' : ''} />
              <Kpi k="stale" n={st.sync_stale} label={c.staleSync} />
              <Kpi k="api" n={st.api_last_minute} label={`${c.apiMinute} (≤ ${st.api_limit_per_minute})`} />
            </div>

            <div className="ac-grid2" style={{ marginTop: 16 }}>
              <div className="ac-card" data-spend="1">
                <h2 className="ac-card-title">{c.spend}</h2>
                <div style={{ display: 'grid', gap: 12 }}>
                  <Meter label={`HioBuy · ${c.today}`} used={sp.hiobuy_units_day} cap={s.hiobuy_units_day} unit={c.units} />
                  <Meter label={`HioBuy · ${c.month}`} used={sp.hiobuy_units_month} cap={s.hiobuy_units_month} unit={c.units} />
                  <Meter label={`Gemini · ${c.today}`} used={sp.gemini_usd_day} cap={s.gemini_usd_day} unit="USD" d={2} />
                  <Meter label={`Gemini · ${c.month}`} used={sp.gemini_usd_month} cap={s.gemini_usd_month} unit="USD" d={2} />
                  <Meter label={`${isAr ? 'الإجمالي' : 'Overall'} · ${c.today}`} used={sp.usd_day} cap={s.daily_budget_usd} unit="USD" d={2} />
                  <Meter label={`${isAr ? 'الإجمالي' : 'Overall'} · ${c.month}`} used={sp.usd_month} cap={s.monthly_budget_usd} unit="USD" d={2} />
                  <div style={{ fontSize: 12.5 }}>{c.remaining} ({c.month}): <b dir="ltr">{fmt(Math.max(0, Number(s.monthly_budget_usd) - Number(sp.usd_month)), 2)} USD · {fmt(Math.max(0, Number(s.hiobuy_units_month) - Number(sp.hiobuy_units_month)))} {c.units}</b></div>
                  <div style={{ fontSize: 12.5 }}>{c.perImport}: <b dir="ltr">{usdPerImport == null ? '—' : `${fmt(usdPerImport, 3)} USD`}</b>{s.hiobuy_usd_per_unit == null ? <span style={{ color: 'var(--ac-faint)' }}> ({isAr ? 'Gemini فقط' : 'Gemini only'})</span> : null}</div>
                  <p style={{ fontSize: 11.5, color: 'var(--ac-faint)', margin: 0 }} data-budget-note="1">{c.budgetNote}</p>
                </div>
              </div>

              <div className="ac-card" data-controls="1">
                <h2 className="ac-card-title">{c.controls}</h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {s.enabled
                    ? <button className="ac-btn ac-btn-sm" data-ingest-action="disable" onClick={() => patch({ enabled: false })}>{c.disable}</button>
                    : <button className="ac-btn ac-btn-sm ac-btn-primary" data-ingest-action="enable" onClick={() => patch({ enabled: true })}>{c.enable}</button>}
                  {s.paused
                    ? <button className="ac-btn ac-btn-sm" data-ingest-action="resume" onClick={() => control('resume')}>{c.resume}</button>
                    : <button className="ac-btn ac-btn-sm" data-ingest-action="pause" onClick={() => control('pause')}>{c.pause}</button>}
                  {s.emergency_stop
                    ? <button className="ac-btn ac-btn-sm" data-ingest-action="release" onClick={() => control('release')}>{c.release}</button>
                    : <button className="ac-btn ac-btn-sm" data-ingest-action="stop" style={{ color: 'var(--ac-danger)' }} onClick={() => control('stop')}>{c.stop}</button>}
                  <button className="ac-btn ac-btn-sm" data-ingest-action="schedule" disabled={!s.enabled || s.paused || s.emergency_stop} onClick={() => control('schedule')}>{c.runNow}</button>
                </div>
                <div style={{ display: 'grid', gap: 8, marginTop: 14, fontSize: 13 }}>
                  {[['discovery_enabled', c.discovery], ['sync_enabled', c.sync], ['auto_publish', c.autoPublish]].map(([k, label]) => (
                    <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="checkbox" data-toggle={k} checked={!!s[k]} onChange={(e) => patch({ [k]: e.target.checked })} />{label}
                    </label>
                  ))}
                </div>
                <h3 className="ac-card-title" style={{ marginTop: 18 }}>{c.rules}</h3>
                <p style={{ fontSize: 12.5, margin: 0, lineHeight: 1.7 }} data-rules="1">{c.rulesText}</p>
                <Link to="/admin2/market-products?review=pending_review" style={{ fontSize: 12.5 }} data-review-link="1">{c.reviewLink} ({fmt(st.pending_review)})</Link>
              </div>
            </div>

            <div className="ac-card" style={{ marginTop: 16 }}>
              <h2 className="ac-card-title">{c.budgets}</h2>
              <NumberFields fields={BUDGET_FIELDS} s={s} draft={draft} setDraft={setDraft} isAr={isAr} />
              <h2 className="ac-card-title" style={{ marginTop: 16 }}>{c.pace}</h2>
              <NumberFields fields={PACE_FIELDS} s={s} draft={draft} setDraft={setDraft} isAr={isAr} />
              <button className="ac-btn ac-btn-sm ac-btn-primary" style={{ marginTop: 12 }} data-save-settings="1" disabled={!Object.keys(draft).length} onClick={saveNumbers}>{c.save}</button>
            </div>

            <div className="ac-grid2" style={{ marginTop: 16 }}>
              <div className="ac-card">
                <h2 className="ac-card-title">{c.queue}</h2>
                <div className="ac-tablewrap">
                  <table className="ac-table" data-queue="1">
                    <thead><tr><th />{statuses.map((x) => <th key={x}>{c.st[x]}</th>)}</tr></thead>
                    <tbody>{kinds.map((k) => (
                      <tr key={k}><td>{c.kind[k]}</td>{statuses.map((x) => <td key={x} dir="ltr">{fmt(queue[`${k}:${x}`] ?? 0)}</td>)}</tr>
                    ))}</tbody>
                  </table>
                </div>
                <h2 className="ac-card-title" style={{ marginTop: 16 }}>{c.dead}</h2>
                {(st.dead_jobs ?? []).length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--ac-faint)' }}>{c.none}</div> : (
                  <div style={{ display: 'grid', gap: 6 }} data-dead-jobs="1">
                    {st.dead_jobs.map((j) => (
                      <div key={j.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                        <span>{c.kind[j.kind]}{j.type_code ? ` · ${marketCategoryLabel(j.type_code, lang)}` : ''}</span>
                        <span dir="ltr" style={{ color: 'var(--ac-faint)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.last_error}</span>
                        <button className="ac-btn ac-btn-sm" data-retry={j.id} onClick={async () => { const r = await ingestRetry(j.id); flash(r.ok ? c.saved : r.error); load(); }}>{c.retry}</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="ac-card">
                <h2 className="ac-card-title">{c.categories}</h2>
                {(st.categories ?? []).length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--ac-faint)' }}>{c.none}</div> : (
                  <table className="ac-table" data-categories="1">
                    <thead><tr><th /><th>{c.added}</th><th>{c.total}</th></tr></thead>
                    <tbody>{st.categories.map((r) => <tr key={r.category}><td>{marketCategoryLabel(r.category, lang)}</td><td dir="ltr">{fmt(r.added)}</td><td dir="ltr">{fmt(r.total)}</td></tr>)}</tbody>
                  </table>
                )}
                <h2 className="ac-card-title" style={{ marginTop: 16 }}>{c.daily}</h2>
                {(st.daily ?? []).length === 0 ? <div style={{ fontSize: 12.5, color: 'var(--ac-faint)' }}>{c.none}</div> : (
                  <table className="ac-table" data-daily="1">
                    <tbody>{st.daily.map((r) => <tr key={r.day}><td dir="ltr">{r.day}</td><td dir="ltr">{fmt(r.hiobuy_units)} {c.units}</td><td dir="ltr">{fmt(r.usd, 3)} USD</td></tr>)}</tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {toast && <div className="ac-toast" data-toast="1">{toast}</div>}
    </ConsoleShell>
  );
}

// The daily Gemini cap (owner, 2026-10-31): one cap across every caller, customers first —
// imports stop at their ceiling, checks at theirs, customers keep the rest of the day.
const LANE = { priority: ['العملاء (ترجمة، مساعد، بحث بالصور)', 'Customers (translation, assistants, photo search)'], check: ['فحص المنتجات الحالية', 'Checks on existing products'], import: ['منتجات جديدة', 'New imports'] };
function AiBudgetCard({ isAr }) {
  const [b, setB] = useState(null);
  const [cap, setCap] = useState('');
  const load = useCallback(async () => setB(await aiBudget()), []);
  useEffect(() => { load(); }, [load]);
  if (!b) return null;
  const used = Number(b.used_usd || 0), capUsd = Number(b.cap_usd || 0);
  const pct = capUsd > 0 ? Math.round((100 * used) / capUsd) : 0;
  const tone = pct >= 100 ? '#B3261E' : pct >= 80 ? '#9A6B12' : '#2E7D32';
  return (
    <div className="ac-card" data-ai-budget={pct} style={{ marginBottom: 16 }}>
      <h2 className="ac-card-title">{isAr ? `استهلاك Gemini اليوم (${b.day}، بتوقيت UTC)` : `Gemini spend today (${b.day}, UTC)`}</h2>
      <div style={{ fontSize: 22, fontWeight: 800, color: tone }} dir="ltr">${used.toFixed(2)} / ${capUsd.toFixed(2)} · {pct}%</div>
      <div style={{ fontSize: 12.5, margin: '6px 0' }}>
        {['priority', 'check', 'import'].map((l) => (
          <div key={l} data-ai-lane={l}>{LANE[l][isAr ? 0 : 1]}: <b dir="ltr">${Number(b.by_lane?.[l] || 0).toFixed(2)}</b>
            {l === 'check' && <span style={{ color: '#666' }}> · {isAr ? 'يتوقف عند' : 'stops at'} ${Number(b.check_ceiling_usd).toFixed(2)}</span>}
            {l === 'import' && <span style={{ color: '#666' }}> · {isAr ? 'يتوقف عند' : 'stops at'} ${Number(b.import_ceiling_usd).toFixed(2)}</span>}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="ac-input" style={{ width: 110 }} inputMode="decimal" placeholder={isAr ? 'سقف يومي $' : 'Daily cap $'} value={cap} onChange={(e) => setCap(e.target.value)} dir="ltr" />
        <button className="ac-btn ac-btn-sm" disabled={!(Number(cap) >= 0) || cap === ''} onClick={async () => { await aiBudgetUpdate({ daily_cap_usd: Number(cap) }); setCap(''); load(); }}>{isAr ? 'حفظ السقف' : 'Save cap'}</button>
        <span style={{ fontSize: 12, color: '#666' }}>{isAr ? 'التكلفة من عدد التوكنز الذي يرجعه Gemini بسعره المعلن.' : "Cost from Gemini's own token counts at list price."}</span>
      </div>
    </div>
  );
}
